import classNames from 'classnames';
import isEmpty from 'lodash/fp/isEmpty';
import omit from 'lodash/fp/omit';
import React, { useState } from 'react';

import Button from '@rio-cloud/rio-uikit/Button';
import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import SortArrows from '@rio-cloud/rio-uikit/SortArrows';
import SortDirection, { type SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import { naturalSortByProperty } from '@rio-cloud/rio-uikit/SortUtils';
import TableCardsSorting from '@rio-cloud/rio-uikit/TableCardsSorting';
import TableSearch from '@rio-cloud/rio-uikit/TableSearch';
import TableSettingsDialog from '@rio-cloud/rio-uikit/TableSettingsDialog';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';

import { INavigationTip, infractionTypeOptions } from '../../../../../types/navigationTips';

const defaultColumnOrder = ['infractionType', 'text', 'videoLink'];
const disabledColumns: string[] = [];

type ColumnLabel = {
  [key: string]: string;
};

const columnLabels: ColumnLabel = {
  infractionType: 'Tipo de Infração',
  text: 'Texto',
  videoLink: 'Link do Vídeo',
};

type ColumnDetails = {
  [key: string]: number;
};

type ColumnDetailsMap = {
  [key: string]: ColumnDetails;
};

const demoColumnsDetails: ColumnDetailsMap = {
  infractionType: {
    width: 200,
    defaultWidth: 200,
    maxWidth: 300,
  },
  text: {
    width: 400,
    defaultWidth: 400,
    maxWidth: 600,
  },
  videoLink: {
    width: 200,
    defaultWidth: 200,
    maxWidth: 300,
  },
};

interface CustomTableProps {
  data: INavigationTip[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (key: string) => void;
  searchValue: string;
  onSearchChange: (v: string) => void;
  onEditTip: (tip: INavigationTip) => void;
  onDeleteTip: (tipId: string) => void;
  onCreateEdit?: () => void;
}

const CustomTable: React.FC<CustomTableProps> = ({
  data,
  sortBy,
  sortDir,
  onSort,
  searchValue,
  onSearchChange,
  onEditTip,
  onDeleteTip,
  onCreateEdit,
}) => {
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsDetails, setColumnsDetails] = useState(demoColumnsDetails);
  const [viewType, setViewType] = useState<TableViewTogglesViewType>(TableViewToggles.VIEW_TYPE_TABLE);
  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);

  const handleToggleTableSettingsDialog = () => setShowTableSettingsDialog(!showTableSettingsDialog);
  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) => setViewType(newViewType);
  const handleSearchValueChange = (newSearchValue: string) => onSearchChange(newSearchValue);

  const handleColumnChange = (
    newColumnOrder: string[],
    newHiddenColumns: string[],
    newColumnsDetails = columnsDetails
  ) => {
    setColumnOrder(newColumnOrder);
    setHiddenColumns(newHiddenColumns);
    setColumnsDetails(newColumnsDetails);
  };

  const handleColumnDetailsChange = (column: string, newColumnDetails: ColumnDetails) => {
    const updatedColumnsDetails = { ...columnsDetails };
    updatedColumnsDetails[column] = newColumnDetails;
    setColumnsDetails(updatedColumnsDetails);
  };

  const getSortDir = (sortDir: SortDirectionType, sortBy: string, previousSortBy: string) => {
    if (sortBy === previousSortBy) {
      return sortDir === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
    }
    return SortDirection.ASCENDING;
  };

  const handleSortChange = (event: React.MouseEvent<HTMLElement>) => {
    const newSortBy = event.currentTarget.getAttribute('data-sortby');
    if (newSortBy) {
      handleCardSortChange(newSortBy, getSortDir(sortDir as SortDirectionType, newSortBy, sortBy));
    }
  };

  const handleCardSortChange = (newSortBy: string, newSortDir: SortDirectionType) => {
    onSort(newSortBy);
  };

  const renderTableHead = (column: string, label: string, sortByColumn: string, sortDirection: SortDirectionType) => {
    const tableHeadClassNames = classNames('user-select-none', 'sort-column');

    return (
      <th
        key={column}
        className={tableHeadClassNames}
        onClick={handleSortChange}
        data-field={column}
        data-sortby={column}
        title={label}
      >
        <span>
          {sortByColumn === column ? <SortArrows direction={sortDirection} /> : <SortArrows />}
          <span>{label}</span>
        </span>
      </th>
    );
  };

  const renderTableCaption = (column: string, columnDetails: ColumnDetails) => {
    const style = columnDetails?.width
      ? {
        minWidth: columnDetails.width,
        width: columnDetails.width,
      }
      : {};

    return <col key={column} style={style} />;
  };

  const getInfractionTypeLabel = (type: string) => {
    const option = infractionTypeOptions.find(opt => opt.value === type);
    return option ? option.label : type;
  };

  const renderTableRow = (tip: INavigationTip, index: number) => (
    <tr key={`${tip.id}-${index}`}>
      <td className="font-bold">{tip.description || getInfractionTypeLabel(tip.infractionType)}</td>
      <td>
        <div
          className="max-width-400 overflow-hidden text-ellipsis whitespace-nowrap cursor-help"
          title={tip.text}
        >
          {tip.text}
        </div>
      </td>
      <td>
        <div className="max-width-200 overflow-hidden text-ellipsis whitespace-nowrap">
          {tip.videoLink ? (
            <a
              href={tip.videoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-color-primary text-decoration-underline"
              title={tip.videoLink}
            >
              Ver vídeo
            </a>
          ) : (
            <span className="text-color-gray font-style-italic">Não informado</span>
          )}
        </div>
      </td>
      <td className="table-action">
        <ButtonDropdown
          title={<span className="rioglyph rioglyph-option-vertical" />}
          variant="link"
          iconOnly
          items={[
            {
              value: (
                <div>
                  <span className="rioglyph rioglyph-pencil margin-right-10" />
                  <span>Editar</span>
                </div>
              ),
              onSelect: () => onEditTip?.(tip),
            },
            {
              value: (
                <div>
                  <span className="rioglyph rioglyph-trash margin-right-10" />
                  <span>Excluir</span>
                </div>
              ),
              onSelect: () => onDeleteTip?.(tip.id),
            },
          ]}
        />
      </td>
    </tr>
  );

  const columns = columnOrder.filter(name => !hiddenColumns.includes(name));

  const withoutHidden = omit(hiddenColumns);
  const filteredRows = data.map(tip => ({ ...withoutHidden(tip) })) as INavigationTip[];

  const searchResult = !searchValue
    ? filteredRows
    : filteredRows.filter((row: Partial<INavigationTip>) =>
      columns.some(col => {
        const value = row[col as keyof INavigationTip];
        if (value == null) {
          return false;
        }
        return row[col as keyof INavigationTip]?.toString().toLowerCase().includes(searchValue.toLowerCase());
      })
    );

  const rows = sortBy ? naturalSortByProperty(searchResult, sortBy as keyof INavigationTip, sortDir as SortDirectionType) : searchResult;

  const tableClassNames = classNames(
    'table',
    'table-layout-fixed',
    'table-column-overflow-hidden',
    'table-bordered',
    'table-sticky',
    'table-head-filled',
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && 'table-cards table-single-card',
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && 'table-cards table-multi-cards'
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map(column => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortBy,
      disabled: false,
    };
  });

  return (
    <div>
      <TableToolbar>
        <div className="table-toolbar-container">
          <div className="table-toolbar-group-left">
            <div className="table-toolbar-column">
              <div className="btn-toolbar table-btn-toolbar">
                <Button bsStyle={Button.PRIMARY} iconName="rioglyph-plus" onClick={onCreateEdit}>
                  Nova Dica
                </Button>
              </div>
            </div>
            <div className="table-toolbar-column">
              <TableSearch
                value={searchValue}
                onChange={handleSearchValueChange}
                placeholder="Buscar nas dicas..."
              />
            </div>
          </div>
          <div className="table-toolbar-group-right">
            <div className="table-toolbar-column table-toolbar-column-spacer">
              <label className="table-toolbar-label">Visualização</label>
              <TableViewToggles
                initialViewType={viewType}
                onViewTypeChange={handleViewTypeChange}
                tableViewTooltipContent="Visualização em tabela"
                singleCardViewTooltipContent="Visualização em lista"
                multiCardsViewTooltipContent="Visualização em cards"
              />
            </div>
            <div className="table-toolbar-column">
              <Button iconOnly iconName="rioglyph-settings" onClick={handleToggleTableSettingsDialog} />
            </div>
          </div>
          {showTableSettingsDialog && (
            <TableSettingsDialog
              show={showTableSettingsDialog}
              title="Configurações da tabela"
              onHide={handleToggleTableSettingsDialog}
              onColumnChange={handleColumnChange}
              defaultColumnOrder={defaultColumnOrder}
              defaultHiddenColumns={[]}
              columnOrder={columnOrder}
              hiddenColumns={hiddenColumns}
              columnLabels={columnLabels}
              disabledColumns={disabledColumns}
              closeButtonText="Fechar"
              resetButtonText="Restaurar padrão"
              searchPlaceholder="Buscar por nome da coluna"
              notFoundMessage="Nenhuma coluna encontrada"
              columnsDetails={columnsDetails}
              autoLabel="Automático"
              onColumnDetailsChange={handleColumnDetailsChange}
              onSearchChange={(val: string) => {
              }}
              immediateChange
            />
          )}
        </div>
      </TableToolbar>
      {sortBy && !isViewTypeTable && (
        <TableCardsSorting
          selectOptions={cardSortingSelectOptions}
          sortName={sortBy}
          sortOrder={sortDir as SortDirectionType}
          onSortChange={handleCardSortChange}
        />
      )}
      <div>
        {isEmpty(rows) && !isViewTypeTable && (
          <NotFoundState headline="Nada encontrado" message="Refine sua busca" />
        )}
        <table className={tableClassNames}>
          <colgroup>
            {columns.map(column => renderTableCaption(column, columnsDetails[column]))}
            <col className="table-action" />
          </colgroup>
          <thead>
            <tr>
              {columns.map(column => renderTableHead(column, columnLabels[column], sortBy, sortDir as SortDirectionType))}
              <th className="table-action" />
            </tr>
          </thead>
          <tbody>
            {isEmpty(rows) && isViewTypeTable && (
              <tr>
                <td colSpan={columns.length + 1}>
                  <NotFoundState
                    outerClassName="border-none"
                    headline="Nada encontrado"
                    message="Refine sua busca"
                  />
                </td>
              </tr>
            )}
            {rows.map((row: INavigationTip, index: number) => renderTableRow(row, index))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default React.memo(CustomTable); 